import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import type { AgentSessionSummary } from "../../../api/copilotAgentTypes";
import { api } from "../../../api/operationsApi";

const deleteSession = withErrorHandling(api.copilotAgents.deleteSession);

interface SessionHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  onResume: (sessionId: string) => Promise<boolean>;
  sessions: AgentSessionSummary[] | undefined;
  loadFailed: boolean;
  onReload: () => void;
}

const SessionHistoryDrawer = ({
  open,
  onClose,
  onResume,
  sessions,
  loadFailed,
  onReload,
}: SessionHistoryDrawerProps) => {
  const { sendNotificationError } = useNotificationContext();
  const [pendingDeleteId, setPendingDeleteId] = useState<string>();
  const [isResuming, setIsResuming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleResume = useCallback(
    async (sessionId: string) => {
      setIsResuming(true);
      try {
        await onResume(sessionId);
      } finally {
        setIsResuming(false);
      }
    },
    [onResume]
  );

  const closeDeleteDialog = useCallback(() => {
    if (!isDeleting) {
      setPendingDeleteId(undefined);
    }
  }, [isDeleting]);

  const handleDelete = useCallback(async () => {
    if (!pendingDeleteId) {
      return;
    }
    setIsDeleting(true);
    const [, error] = await deleteSession(pendingDeleteId);
    setIsDeleting(false);
    if (error) {
      logError(error, "[SessionHistoryDrawer] deleteSession");
      sendNotificationError(error.message);
      return;
    }
    setPendingDeleteId(undefined);
    onReload();
  }, [pendingDeleteId, onReload, sendNotificationError]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Stack sx={{ width: 380, p: 2, gap: 1, height: "100%" }}>
        <Typography variant="subtitle1">Session history</Typography>
        {sessions === undefined && !loadFailed && (
          <Stack sx={{ alignItems: "center", py: 4 }}>
            <CircularProgress size={20} color="secondary" />
          </Stack>
        )}
        {loadFailed && <Typography color="error">Failed to load sessions.</Typography>}
        {!loadFailed && sessions?.length === 0 && <Typography color="textSecondary">No past sessions.</Typography>}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <List disablePadding>
            {sessions?.map((s) => (
              <ListItem
                key={s.sessionId}
                disablePadding
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => setPendingDeleteId(s.sessionId)}
                    disabled={isResuming || isDeleting || s.isActive}
                  >
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                }
              >
                <ListItemButton
                  onClick={() => handleResume(s.sessionId)}
                  disabled={isResuming || isDeleting || s.isActive}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        <Typography noWrap sx={{ maxWidth: 220 }}>
                          {s.title}
                        </Typography>
                        {s.isActive && <Chip label="Active" color="primary" />}
                      </Stack>
                    }
                    secondary={`${convertISODateTime(s.updatedAtUtc)} · ${s.model} · ${s.messageCount} messages`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Stack>
      <SmallDialog open={pendingDeleteId !== undefined} onClose={closeDeleteDialog}>
        <DialogTitle>Delete this session permanently?</DialogTitle>
        <DialogButtons onCancel={closeDeleteDialog} cancelDisabled={isDeleting}>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={isDeleting}>
            Delete
          </Button>
        </DialogButtons>
      </SmallDialog>
    </Drawer>
  );
};

export default SessionHistoryDrawer;
